import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import TimePresetSelect from "@/components/TimePresetSelect";
import { CheckSquare, Square, Plus, Trash2, Loader2, AlarmClock } from "lucide-react";

function fmtDue(iso) {
  try {
    return new Date(iso).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function isOverdue(iso) {
  try {
    return new Date(iso) < new Date();
  } catch {
    return false;
  }
}

// Phase B2 — Split date+heure séparés (au lieu de datetime-local unique
// catastrophique sur iPad Safari). TimePresetSelect propose des créneaux
// 7:00→19:00 pas 15min + option "Autre…" pour saisie libre.
function defaultDate() {
  const d = new Date();
  d.setSeconds(0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function defaultTime() {
  // Prochain créneau 15 min arrondi + 1h buffer, entre 7:00 et 19:00
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
  let h = d.getHours();
  let m = d.getMinutes();
  if (h < 7) { h = 9; m = 0; }
  if (h >= 19) { h = 9; m = 0; }
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}`;
}

export function TasksBlock({ leadId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState(defaultDate());
  const [dueTime, setDueTime] = useState(defaultTime());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tasks?lead_id=${leadId}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [leadId]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!title.trim() || !dueDate || !dueTime) return;
    setSaving(true);
    try {
      // Combine date + heure locale → ISO UTC pour le backend
      const iso = new Date(`${dueDate}T${dueTime}`).toISOString();
      await api.post("/tasks", { lead_id: leadId, title: title.trim(), due_at: iso });
      setTitle("");
      setDueDate(defaultDate());
      setDueTime(defaultTime());
      toast.success("Tâche ajoutée");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (task) => {
    if (task.status === "done") return; // pas de un-done pour l'instant
    try {
      await api.post(`/tasks/${task.id}/complete`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (task) => {
    try {
      await api.delete(`/tasks/${task.id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const pending = items.filter((t) => t.status === "pending");
  const done = items.filter((t) => t.status === "done");

  return (
    <Card className="border-slate-800 bg-slate-900/40" data-testid="lead-tasks">
      <CardContent className="p-4">
        <div className="mb-3 inline-flex items-center gap-2">
          <AlarmClock className="h-4 w-4 text-[#C4553B]" />
          <span className="text-sm font-semibold text-slate-200">Tâches</span>
          <span className="text-xs text-slate-500">{pending.length} en cours</span>
        </div>

        <form onSubmit={submit} className="mb-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 200))}
            placeholder="Nouvelle tâche…"
            className="w-full border-slate-700 bg-slate-950/40 text-slate-100 sm:flex-1 sm:min-w-[180px]"
            data-testid="task-title-input"
            maxLength={200}
          />
          <div className="flex gap-2">
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="flex-1 border-slate-700 bg-slate-950/40 text-slate-100 sm:w-[150px] sm:flex-none"
              data-testid="task-due-date-input"
            />
            <TimePresetSelect
              value={dueTime}
              onChange={setDueTime}
              testid="task-due-time"
            />
            <Button
              type="submit"
              disabled={saving || !title.trim() || !dueDate || !dueTime}
              className="bg-[#C4553B] text-white hover:bg-[#A94531]"
              data-testid="task-add-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </Button>
          </div>
        </form>

        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-500" data-testid="tasks-empty">
            Aucune tâche pour ce lead.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {pending.map((t) => {
              const overdue = isOverdue(t.due_at);
              return (
                <li
                  key={t.id}
                  className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm ${
                    overdue
                      ? "border-[#A63D2F]/60 bg-[#A63D2F]/10"
                      : "border-slate-800 bg-slate-950/40"
                  }`}
                  data-testid={`task-${t.id}`}
                >
                  <button
                    onClick={() => toggle(t)}
                    className="text-slate-500 hover:text-[#C4553B]"
                    data-testid={`task-toggle-${t.id}`}
                    title="Marquer terminée"
                  >
                    <Square className="h-4 w-4" />
                  </button>
                  <div className="flex-1">
                    <div className="text-slate-100">{t.title}</div>
                    <div className={`text-[11px] ${overdue ? "text-[#E67B6D]" : "text-slate-500"}`}>
                      {overdue ? "⚠ Retard · " : ""}{fmtDue(t.due_at)}
                    </div>
                  </div>
                  <button
                    onClick={() => remove(t)}
                    className="text-slate-600 hover:text-[#E67B6D]"
                    title="Supprimer"
                    data-testid={`task-delete-${t.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })}
            {done.length > 0 && (
              <details className="pt-2">
                <summary className="cursor-pointer text-[11px] text-slate-500 hover:text-slate-300">
                  {done.length} terminée{done.length > 1 ? "s" : ""}
                </summary>
                <ul className="mt-1 space-y-1">
                  {done.map((t) => (
                    <li key={t.id} className="flex items-center gap-2 rounded-md border border-slate-800 bg-slate-950/20 px-2.5 py-1 text-xs text-slate-500 line-through">
                      <CheckSquare className="h-3.5 w-3.5 text-[#7A8450]" />
                      {t.title}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
